import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import moment from 'moment-timezone';
import { push } from 'connected-react-router';

import { actions } from './redux/actions';
import { actions as capsActions } from '../ShipperCaps/redux/actions';
import { GET_DEVICES, GET_DEVICES_RESULT } from './redux/constants';
import {
  CHANGE_DEVICE_STATUS_HISTORY,
  DOWNLOAD_REPORT_DATA_ERROR,
} from '../ShipperCaps/redux/constants';
import { PageHeader } from '../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../components/PageHeader/Button';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import { TextInput } from '../../components/PageHeader/TextInput';
import Strings from '../../Strings';
import {
  getCapChargeText,
  toolTipForTable,
  getVitalsData,
  downloadFileOnly,
  convertUnixEpochToHumanReadable,
} from '../../utils';
import Select from '../../components/Select';
import AdvancedFilters, { Filter } from '../../components/AdvancedFilters/AdvancedFilters';
import AdvancedFiltersBar from '../../components/AdvancedFilters/AdvancedFiltersBar';
import { getFiltersForRequest, urlInjected } from '../../components/AdvancedFilters/helpers';
import { getFiltersDefinition } from '../../components/AdvancedFilters/FiltersDefinition';
import { isCapManager, isDoctor, isOrganizationManager, isShipper, isSuperUser } from '../../utils/userRoles';
import { openModalAction } from '../../actions/modal';
import { notificationActions } from '../../components/Notification/redux/actions';
import { ActionWidget, ActionButton, ItemSelectorHelper } from '../../components/ActionWidget';
import { DateFilters } from '../../utils/DateFilters';
import { getUrlForDevice } from '../SuperUser/DeviceManagement/HubDeviceEvents';
import { DeviceTypes } from '../../utils/deviceTypes';
import OrgSelect, { NO_ORG } from '../../components/OrgSelect/OrgSelect';
import { useGlobalPageSize } from '../../utils/tableUtils';

const mapStateToPropsDeviceView = state => {
  return {
    devices: state.shipper.devices.data,
    pagination: state.shipper.devices.pagination,
    filters: state.entities.advancedFilters.filters.items,
    orgIds: state.entities?.orgFilter?.orgIds,
    orgs: state.entities?.orgFilter?.orgs,
  };
};

const mapDispatchToPropsDeviceView = dispatch => ({
  onGetDevices: pageRequest => dispatch(actions.getDevices(pageRequest)),
  onNavigate: path => dispatch(push(path)),
  onAttachDevice: (type, postAction) =>
    dispatch(
      openModalAction('attach-cap-to-org', {
        action: actions.getDevices,
        actionType: GET_DEVICES,
        type,
        postAction,
      }),
    ),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  downloadEvents: params => dispatch(capsActions.downloadReport(params)),
  downloadDetails: params => dispatch(capsActions.downloadDetails(params)),
  openDateRangeSelectionModal: (actionOnDone, downloadRequest, orgName) =>
    dispatch(
      openModalAction('date-range-selection-for-events-reports-modal', {
        actionOnDone,
        simplified: true,
        filters: downloadRequest,
        orgName,
      }),
    ),
  showNotification: (message, timeout) => dispatch(notificationActions.show(message, timeout)),
  changeDeviceStatus: data => dispatch(openModalAction('change-state', data)),
});

function DeviceView(props) {
  const {
    deviceClass,
    onGetDevices,
    pagination,
    devices,
    onNavigate,
    filters,
    onAttachDevice,
    openConfirmModal,
    downloadEvents,
    downloadDetails,
    openDateRangeSelectionModal,
    showNotification,
    filtersDisabled,
    changeDeviceStatus,
    orgIds,
    orgs,
  } = props;

  const [isLoading, setIsLoading] = React.useState(true);
  const [openActionWidget, setOpenActionWidget] = React.useState(false);
  const [keyForItemCheckboxes, setKeyForItemCheckboxes] = React.useState(false);
  const [multiselectedItems, setMultiselectedItems] = React.useState(false);

  const globalPageSize = useGlobalPageSize();
  const request = React.useRef({
    deviceType: deviceClass,
    limit: globalPageSize,
    offset: 0,
    search: '',
    filters: [],
    orgId: (orgIds && orgIds.length > 0 && !orgIds.includes(NO_ORG)) ? orgIds : undefined,
    status: orgIds?.includes(NO_ORG) ? 'unassigned' : undefined,
  });

  const itemSelectorHelper = React.useRef(new ItemSelectorHelper());
  const loadingRef = React.useRef(false);
  const refreshEnabled = React.useRef(true);
  const prevOrgIds = React.useRef(orgIds);
  const prevFilters = React.useRef(filters);
  const isInitialMount = React.useRef(true);
  const hasFiltersInUrl = urlInjected(window.location.search);
  const hasSeenHydratedFilters = React.useRef(false);

  const resetActionWidget = React.useCallback(() => {
    setKeyForItemCheckboxes(prev => !prev);
    setOpenActionWidget(false);
    itemSelectorHelper.current.clearItems();
  }, []);

  const getDevices = React.useCallback(() => {
    if (loadingRef.current) {
      return;
    }
    loadingRef.current = true;
    setIsLoading(true);

    onGetDevices(request.current).then(resp => {
      loadingRef.current = false;
      resetActionWidget();
      if (resp.type === GET_DEVICES_RESULT) setIsLoading(false);
    });
  }, [onGetDevices, resetActionWidget]);

  const filtersDebounced = React.useMemo(
    () =>
      _.debounce(() => {
        const newFiltersForRequest = getFiltersForRequest(filters);
        if (JSON.stringify(newFiltersForRequest) !== JSON.stringify(request.current.filters)) {
          request.current.offset = 0;
          request.current.filters = newFiltersForRequest;
          getDevices();
        }
      }, 1000),
    [filters, getDevices],
  );

  const onFiltersChange = React.useCallback(
    filtersParam => {
      return filtersDebounced(filtersParam);
    },
    [filtersDebounced],
  );

  React.useEffect(() => {
    // Initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (filtersDisabled || !urlInjected(window.location.search)) {
        getDevices();
      }
      return;
    }

    // orgIds changed
    if (orgIds !== prevOrgIds.current) {
      prevOrgIds.current = orgIds;
      request.current.orgId = (orgIds && orgIds.length > 0 && !orgIds.includes(NO_ORG)) ? orgIds : undefined;
      request.current.offset = 0;
      if (orgIds?.includes(NO_ORG)) {
        delete request.current.orgId;
        request.current.status = 'unassigned';
      } else {
        delete request.current.status;
      }
      getDevices();
      return;
    }

    // filters changed
    if (filters !== prevFilters.current) {
      if (hasFiltersInUrl && !hasSeenHydratedFilters.current && filters.length === 0) {
        return;
      }
      if (filters.length > 0) {
        hasSeenHydratedFilters.current = true;
      }
      prevFilters.current = filters;
      onFiltersChange(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgIds, filters]);

  const onRefresh = React.useCallback(() => {
    if (refreshEnabled.current) {
      refreshEnabled.current = false;
      setTimeout(() => {
        refreshEnabled.current = true;
      }, 1000);
      request.current.offset = 0;
      getDevices();
    }
  }, [getDevices]);

  const onDateRangeChanged = React.useCallback(
    dateRange => {
      if (dateRange) {
        request.current.lastUpdatedStart = dateRange.dates.startDate();
        request.current.lastUpdatedEnd = dateRange.dates.endDate();
      } else {
        request.current.lastUpdatedStart = '';
        request.current.lastUpdatedEnd = '';
      }
      request.current.offset = 0;

      getDevices();
    },
    [getDevices],
  );

  const onSearchQueryChangeDebounced = React.useMemo(
    () =>
      _.debounce(query => {
        request.current.search = query;
        request.current.offset = 0;
        getDevices();
      }, 1000),
    [getDevices],
  );

  const onSearchQueryChange = React.useCallback(
    query => {
      return onSearchQueryChangeDebounced(query);
    },
    [onSearchQueryChangeDebounced],
  );

  const onTextInputChange = React.useCallback(
    e => {
      onSearchQueryChange(e.target.value);
    },
    [onSearchQueryChange],
  );

  const onOffsetChange = React.useCallback(
    offset => {
      request.current.offset = offset;
      getDevices();
    },
    [getDevices],
  );

  const onPageSizeChange = React.useCallback(
    newSize => {
      request.current.offset = 0;
      request.current.limit = newSize;
      getDevices();
    },
    [getDevices],
  );

  const onRowSelection = React.useCallback(
    device => {
      if (!device) return;
      const url = getUrlForDevice(device, deviceClass);
      onNavigate(`${url}?lastActivity=${device.lastActivity}`);
    },
    [deviceClass, onNavigate],
  );

  const onDownloadBtnClickSuccessHandler = React.useCallback(
    req => {
      if (req?.type === DOWNLOAD_REPORT_DATA_ERROR) {
        showNotification(req?.error);
      } else {
        const receivedName = req.headers['content-disposition'].split('filename=')[1];
        const fileName = `${moment().toString() + receivedName}`;
        downloadFileOnly(req, fileName);
      }
    },
    [showNotification],
  );

  const onDownload = React.useCallback(() => {
    const downloadHandler = params => {
      const paramsWithType = params;
      paramsWithType.deviceType = deviceClass;

      switch (params.selectedOption) {
        case 'events':
          delete paramsWithType.selectedOption;
          downloadEvents(params).then(onDownloadBtnClickSuccessHandler);
          break;
        case 'details':
          delete paramsWithType.selectedOption;
          paramsWithType.limit = 100000;
          paramsWithType.offset = 0;
          downloadDetails(params).then(onDownloadBtnClickSuccessHandler);
          break;
        case 'vitals':
          delete paramsWithType.selectedOption;
          paramsWithType.simplified = true;
          downloadEvents(params).then(onDownloadBtnClickSuccessHandler);
          break;
        default:
          downloadEvents(params).then(onDownloadBtnClickSuccessHandler);
      }
      showNotification('Download request sent, please wait...');
    };
    const downloadRequest = { ...request.current };
    delete downloadRequest.offset;
    delete downloadRequest.limit;
    if (!downloadRequest.lastUpdatedStart) {
      downloadRequest.lastUpdatedStart = DateFilters.AllTime.dates.startDate();
    }
    if (!downloadRequest.lastUpdatedEnd) {
      downloadRequest.lastUpdatedEnd = DateFilters.AllTime.dates.endDate();
    }

    let orgName = '';
    if (downloadRequest.orgId) {
      orgName = orgs?.find(o => o.masked_id === downloadRequest.orgId)?.name;
      if (downloadRequest.orgId === NO_ORG) {
        orgName = Strings.patientDashboard.noOrg;
      }
    }
    openDateRangeSelectionModal(downloadHandler, downloadRequest, orgName);
  }, [deviceClass, downloadEvents, downloadDetails, openDateRangeSelectionModal, showNotification, orgs, onDownloadBtnClickSuccessHandler]);

  const getHeaderComponents = React.useCallback(() => {
    const headerFilters = [<Filter key="status-filter" definition={getFiltersDefinition().Status} />];
    if (
      deviceClass !== DeviceTypes.Spirometer.id &&
      deviceClass !== DeviceTypes['Hailie Inhaler'].id &&
      deviceClass !== DeviceTypes['Air Quality Monitor'].id
    ) {
      headerFilters.push(<Filter key="battery-filter" definition={getFiltersDefinition().Battery} />);
    }
    return (
      <React.Fragment>
        <OrgSelect addNoOrg />
        <Button class="refresh" onClick={onRefresh} />
        <TextInput className="search" placeholder={Strings.search} onChange={onTextInputChange} />
        <Select
          data={[
            DateFilters.AllTime,
            DateFilters.Last48Hours,
            DateFilters.ThisMonth,
            DateFilters.Last30Days,
            DateFilters.LastMonth,
            DateFilters.Last6Months,
            DateFilters.ThisYear,
            DateFilters.Last12Months,
          ]}
          onChange={onDateRangeChanged}
          defaultValue={DateFilters.AllTime.value}
        />
        {(isSuperUser() || isShipper()) && (
          <Button
            class={HEADER_BUTTON_DARK_BLUE}
            onClick={() => onAttachDevice(deviceClass, getDevices)}
            title={Strings.attachCap}
          />
        )}
        <AdvancedFilters>{headerFilters}</AdvancedFilters>
        {(isCapManager() || isOrganizationManager() || isSuperUser() || isDoctor()) && (
          <Button class="download" onClick={onDownload} />
        )}
      </React.Fragment>
    );
  }, [deviceClass, onTextInputChange, onDateRangeChanged, onAttachDevice, getDevices, onDownload]);

  const onUnassignFromOrg = React.useCallback(
    (event, cap) => {
      event.preventDefault();
      event.stopPropagation();

      const requestData = {
        device_id: cap,
      };

      const data = {
        title: (
          <span>
            {Strings.deleteCapWarning} <b>{cap}</b> from organization <b>{cap.organization}</b>?
          </span>
        ),
        onConfirmAction: capsActions.deleteCaps(requestData),
        confirmPostAction: getDevices,
        onCancelAction: null,
      };
      openConfirmModal(data);
    },
    [getDevices, openConfirmModal],
  );

  const onStatusChange = React.useCallback(
    (event, cap) => {
      event.preventDefault();
      event.stopPropagation();

      const data = {
        id: cap,
        propertyForApi: 'status',
        action: capsActions.changeDeviceStatus,
        actionType: CHANGE_DEVICE_STATUS_HISTORY,
        title: Strings.changeDeviceState,
        state: StateTypes.device.id,
        onSuccess: getDevices,
      };
      changeDeviceStatus(data);
    },
    [getDevices, changeDeviceStatus],
  );

  const itemChecked = React.useCallback(
    ({ target }) => {
      const data = {
        deviceId: target.id,
        organization: target.dataset.org,
      };
      if (target.checked === true) {
        itemSelectorHelper.current.addItem(data.deviceId);
      } else {
        itemSelectorHelper.current.removeItem(data.deviceId);
      }

      if (itemSelectorHelper.current.getItems().length !== 0) {
        setOpenActionWidget(true);
      } else {
        setOpenActionWidget(false);
      }

      setMultiselectedItems(itemSelectorHelper.current.isMultiselect());
    },
    [],
  );

  const columns = React.useMemo(() => {
    const cols = [];

    cols.push(
      columnHelper.accessor('deviceId', {
        header: Strings.deviceId,
        cell: info => {
          const e = info.row.original;
          const prefix = e.manufacturer.concat('.', e.model);
          let trueDeviceId = e.deviceId.replace(prefix, '');
          if (trueDeviceId.startsWith('.')) trueDeviceId = trueDeviceId.slice(1);
          if (isShipper() || isSuperUser()) {
            return (
              <React.Fragment>
                <div className="cell-with-select">
                  <div className="selector" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="item-checkbox"
                      id={e.deviceId}
                      data-org={e.organization}
                      onChange={itemChecked}
                      key={keyForItemCheckboxes}
                    />
                  </div>
                  <div className="selector-label">{trueDeviceId}</div>
                </div>
              </React.Fragment>
            );
          }
          return trueDeviceId;
        },
      }),
    );

    if (deviceClass !== DeviceTypes.Spirometer.id) {
      cols.push(
        columnHelper.accessor('name', {
          header: Strings.deviceName,
          cell: info => (info.getValue() ? info.getValue() : '-'),
        }),
      );
    }

    cols.push(
      columnHelper.accessor('deviceModel', {
        header: Strings.deviceModel,
        cell: info => info.getValue() || '-',
        accessorFn: row => row.manufacturer.concat(' ', row.model),
      }),
    );

    if (orgs?.length > 1) {
      cols.push(
        columnHelper.accessor('organization', {
          header: Strings.organizationName,
          cell: info => info.getValue() || '-',
        }),
      );
    }

    if (
      deviceClass !== DeviceTypes.Spirometer.id &&
      deviceClass !== DeviceTypes['Hailie Inhaler'].id
    ) {
      cols.push(
        columnHelper.accessor('hubs', {
          header: Strings.devicesPlural.hub,
          cell: info => {
            const hubs = info.getValue();
            return hubs?.length > 1 ? toolTipForTable(`${hubs[0]}...`, hubs.join(', ')) : hubs;
          },
        }),
      );
    }

    cols.push(
      columnHelper.accessor('kitId', {
        header: Strings.kitId,
      }),
    );

    if (
      deviceClass !== DeviceTypes.Spirometer.id &&
      deviceClass !== DeviceTypes['Hailie Inhaler'].id &&
      deviceClass !== DeviceTypes['Air Quality Monitor'].id
    ) {
      cols.push(
        columnHelper.accessor('batteryPercentage', {
          header: Strings.battery.battery,
          cell: info => getCapChargeText(info.getValue()),
        }),
      );
    }

    cols.push(
      columnHelper.accessor('lastActivity', {
        header: Strings.lastActivity,
        cell: info => (info.getValue() ? convertUnixEpochToHumanReadable(info.getValue()) : '-'),
      }),
    );

    cols.push(
      columnHelper.accessor('lastReading', {
        header: Strings.lastReading,
        cell: info => (!_.isEmpty(info.getValue()) ? getVitalsData(info.getValue()) : '-'),
      }),
    );

    cols.push(
      columnHelper.accessor('status', {
        header: Strings.status,
        cell: info => info.getValue() || '-',
      }),
    );

    return cols;
  }, [deviceClass, orgs?.length, keyForItemCheckboxes, itemChecked]);

  const headerTitle =
    !isLoading && pagination && devices
      ? Strings.formatString(
          Strings.showingXDevices,
          devices.length,
          pagination.totalRecords ? pagination.totalRecords : 0,
        )
      : Strings.showingWait;

  return (
    <div className="tab-page-content">
      <PageHeader left={headerTitle} right={() => getHeaderComponents()} />
      <AdvancedFiltersBar />
      <Table3
        name="devices"
        key={`devices-${orgs?.length}`}
        uuid={DeviceTypes[deviceClass].tableUuid}
        isLoading={isLoading}
        onRowClick={onRowSelection}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        pagination={pagination}
        data={devices || []}
        enableColumnFiltering
        columns={columns}
      />
      {(isShipper() || isSuperUser()) && (
        <ActionWidget show={openActionWidget}>
          <ActionButton
            img="action-delete"
            tooltiptext="Delete"
            text="Delete"
            action={e => {
              onUnassignFromOrg(e, itemSelectorHelper.current.getFirstItem());
            }}
            disabled={multiselectedItems}
          />
          <ActionButton
            img="action-reset"
            tooltiptext="Change State"
            text="Change State"
            action={e => {
              onStatusChange(e, itemSelectorHelper.current.getFirstItem());
            }}
          />
        </ActionWidget>
      )}
    </div>
  );
}

DeviceView.propTypes = {
  deviceClass: PropTypes.string,
  onGetDevices: PropTypes.func,
  pagination: PropTypes.object,
  devices: PropTypes.array,
  isLoading: PropTypes.bool,
  onNavigate: PropTypes.func,
  filters: PropTypes.array,
  onAttachDevice: PropTypes.func,
  openConfirmModal: PropTypes.func,
  downloadEvents: PropTypes.func,
  downloadDetails: PropTypes.func,
  openDateRangeSelectionModal: PropTypes.func,
  showNotification: PropTypes.func,
  filtersDisabled: PropTypes.bool,
  getOrgs: PropTypes.func,
  changeDeviceStatus: PropTypes.func,
  orgIds: PropTypes.array,
  orgs: PropTypes.array,
};

export default connect(mapStateToPropsDeviceView, mapDispatchToPropsDeviceView)(DeviceView);
